import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Chip, Input, Avatar, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Tooltip } from "@heroui/react";
import { Typography, UniversalTable, IdView } from "../../../../../components";
import { useTranslations } from "../../../../../contexts/translation.context";
import { EmptyContentIc, IcDate, ResetFiltersIc } from "../../../../../components/icons";
import { FilterSelect } from "../../../../../components/ui";
import { TableColumn } from "../../../../../types/account";
import { staffApi, WithdrawalRequest } from "../../../../../api/staff";
import { formatTimeAgo } from "../../../../../utils/formatTimeAgo";
import { getPartnerLevelConfig } from "../../constants";

interface WithdrawalRequestsTabProps {
  partnerId: string;
}

const SEARCH_DEBOUNCE_DELAY = 500; // Debounce delay

// Status colors for withdrawal requests
const STATUS_COLORS = {
  pending: {
    bg: '#FEF3C7',
    text: '#92400E',
    label: 'Ожидание'
  },
  paid: {
    bg: '#D1FAE5',
    text: '#065F46',
    label: 'Оплачено'
  },
  cancel: {
    bg: '#FEE2E2',
    text: '#991B1B',
    label: 'Деактивирован' // Changed from "Отменено"
  }
} as const;

// Role colors matching withdrawal page
const ROLE_COLORS = {
  new: {
    bg: 'rgba(23, 201, 100, 0.2)',
    text: '#17C964'
  },
  base: {
    bg: '#EEEEEF',
    text: '#71717A'
  },
  pro: {
    bg: '#11181C',
    text: '#FFFFFF'
  },
  expert: {
    bg: 'rgba(120, 40, 200, 0.2)',
    text: '#7828C8'
  }
} as const;

/**
 * WithdrawalRequestsTab Component
 *
 * Таб с заявками на вывод средств партнера.
 * Показывает таблицу заявок с возможностью поиска по ID.
 * Полностью основан на структуре из onlyu_admin/src/pages/withdrawal
 */
const WithdrawalRequestsTab: React.FC<WithdrawalRequestsTabProps> = ({ partnerId }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchId, setSearchId] = useState('');
  const [localSearchId, setLocalSearchId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid' | 'cancel'>('all');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchId(localSearchId);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearchId]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return localSearchId !== '' || statusFilter !== 'all';
  }, [localSearchId, statusFilter]);

  // API params
  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      page_size: pageSize,
      owner: partnerId,
    };

    if (searchId) {
      params.search = searchId;
    }

    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }

    return params;
  }, [currentPage, pageSize, partnerId, searchId, statusFilter]);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffApi.getWithdrawalRequests(apiParams);
      setRequests(response.objects);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching withdrawal requests:", error);
      setRequests([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [apiParams]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const columns: TableColumn[] = [
    { key: "id", name: "ID" },
    { key: "account", name: t('withdrawal.account') || 'Аккаунт' },
    { key: "telegram", name: "Telegram" },
    { key: "amount", name: t('withdrawal.amount') || 'Сумма выплаты' },
    { key: "status", name: t('withdrawal.status') || 'Статус' },
    { key: "date", name: t('withdrawal.date') || 'Дата' },
    { key: "actions", name: "" }
  ];

  const formatShortId = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.length <= 4) return trimmed;
    return trimmed.slice(-4);
  };

  const formatDate = (dateString: string) => {
    const utcDate = new Date(dateString);
    if (Number.isNaN(utcDate.getTime())) {
      return '';
    }
    const date = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60 * 24)));
    const diffInHours = Math.max(0, Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));

    if (diffInDays > 0) {
      return t('wallet.timeAgo.daysHours', { days: diffInDays, hours: diffInHours });
    }
    if (diffInHours > 0) {
      return t('wallet.timeAgo.hours', { hours: diffInHours });
    }
    const diffInMinutes = Math.max(0, Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60)));
    return t('wallet.timeAgo.minutes', { minutes: diffInMinutes });
  };

  const handleStatusChange = useCallback(async (id: string, status: 'paid' | 'pending' | 'cancel') => {
    try {
      await staffApi.changeWithdrawalStatus(id, status);
      // Refresh data after status change
      fetchRequests();
    } catch (error) {
      console.error("Error changing withdrawal status:", error);
    }
  }, [fetchRequests]);

  const handleResetFilters = useCallback(() => {
    setLocalSearchId('');
    setSearchId('');
    setStatusFilter('all');
    setCurrentPage(1);
  }, []);

  const statusOptions = useMemo(() => [
    { key: 'all', label: t('withdrawal.allStatuses') || 'Все статусы' },
    { key: 'pending', label: t('withdrawal.pending') || 'Ожидание' },
    { key: 'paid', label: t('withdrawal.paid') || 'Оплачено' },
    { key: 'cancel', label: t('withdrawal.cancel') || 'Деактивирован' },
  ], [t]);

  const renderCell = useCallback((request: WithdrawalRequest, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return (
          <div className="flex py-[8px]">
            <IdView textToCopy={request.id}>
              {formatShortId(request.id) ?? request.id}
            </IdView>
          </div>
        );

      case "account":
        const shortenedAddress = request.address.length > 20
          ? `${request.address.substring(0, 10)}...${request.address.substring(request.address.length - 8)}`
          : request.address;

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={request.owner.avatar}
              name={request.owner.name}
              size="sm"
              className="flex-shrink-0"
            />
            <div className="flex flex-col items-start gap-[4px]">
              <div className="flex items-center gap-[8px]">
                <span
                  className="text-[14px] font-medium text-[#006FEE] cursor-pointer hover:underline"
                  onClick={() => navigate(`/users/partners/${request.owner.id}/balance`)}
                >
                  {request.owner.name}
                </span>
                {request.owner.lvl && (
                  <Chip
                    style={{
                      backgroundColor: getPartnerLevelConfig(request.owner.lvl, t).bg,
                      color: getPartnerLevelConfig(request.owner.lvl, t).text,
                    }}
                    className="h-[20px] px-[8px] text-[12px] font-medium capitalize"
                  >
                    {getPartnerLevelConfig(request.owner.lvl, t).label}
                  </Chip>
                )}
              </div>
              <IdView textToCopy={request.owner.id}>
                {formatShortId(request.owner.id) ?? request.owner.id}
              </IdView>
              <IdView textToCopy={request.address}>
                {shortenedAddress}
              </IdView>
            </div>
          </div>
        );

      case "telegram":
        return (
          <div className="flex items-center gap-[4px]">
            {request.owner.tg_tag ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM11.93 5.48L10.68 11.24C10.59 11.64 10.35 11.74 10.01 11.54L7.95 10.04L6.95 11.01C6.85 11.11 6.76 11.2 6.56 11.2L6.7 9.11L10.45 5.72C10.61 5.58 10.42 5.5 10.21 5.64L5.63 8.58L3.6 7.95C3.18 7.82 3.17 7.55 3.68 7.35L11.41 4.35C11.76 4.22 12.06 4.43 11.93 5.48Z" fill="#229ED9"/>
                </svg>
                <span className="text-[14px] text-[#11181C]">
                  {request.owner.tg_tag}
                </span>
              </>
            ) : (
              <span className="text-[14px] text-[#71717A]">-</span>
            )}
          </div>
        );

      case "amount":
        const amount = typeof request.amount === 'string' ? parseFloat(request.amount) : request.amount;
        const formattedAmount = Number.isFinite(amount) ? amount.toFixed(2) : '0.00';

        return (
          <span className="text-[14px] font-semibold text-[#11181C]">
            ${formattedAmount}
          </span>
        );

      case "status":
        const statusConfig = STATUS_COLORS[request.status] || STATUS_COLORS.pending;
        const statusLabel = t(`withdrawal.${request.status}`) || statusConfig.label;

        return (
          <Chip
            style={{
              backgroundColor: statusConfig.bg,
              color: statusConfig.text,
            }}
            className="h-[24px] px-[12px] text-[12px] font-medium whitespace-nowrap"
          >
            {statusLabel}
          </Chip>
        );

      case "date":
        return (
          <span className="text-[14px] flex items-center whitespace-nowrap text-[#71717A]">
            <IcDate/>
            {formatTimeAgo(request.created_at, t)}
          </span>
        );

      case "actions":
        return (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <Dropdown>
              <DropdownTrigger>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="2.5" r="1.5" fill="#71717A"/>
                    <circle cx="8" cy="8" r="1.5" fill="#71717A"/>
                    <circle cx="8" cy="13.5" r="1.5" fill="#71717A"/>
                  </svg>
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Actions"
                onAction={(key: React.Key) => handleStatusChange(request.id, String(key) as 'paid' | 'pending' | 'cancel')}
              >
                {request.status !== 'paid' && (
                  <DropdownItem key="paid" className="text-success" color="success">
                    {t('withdrawal.markAsPaid') || 'Отметить как оплачено'}
                  </DropdownItem>
                )}
                {request.status !== 'cancel' && (
                  <DropdownItem key="cancel" className="text-danger" color="danger">
                    {t('withdrawal.cancel') || 'Отменить'}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        );

      default:
        return null;
    }
  }, [t, navigate, handleStatusChange]);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <div className="flex flex-col items-center gap-[6px]">
          <EmptyContentIc/>
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? (t('withdrawal.noDataFiltered') || 'По выбранным фильтрам нет заявок на вывод')
              : (t('withdrawal.noData') || 'Нет заявок на вывод')
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? (t('withdrawal.noDataFilteredDescription') || 'Попробуйте изменить параметры фильтров')
              : (t('admin.partners.withdrawal.emptyDescription') || 'У этого партнера пока нет заявок на вывод средств')
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  if (isLoading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px] w-full">
      {/* Filters */}
      <div className="flex items-center gap-[16px]">
        {/* Search by ID */}
        <Input
          type="text"
          placeholder={t('admin.partners.withdrawal.searchById') || 'Поиск по ID'}
          value={localSearchId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearchId(e.target.value)}
          endContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          classNames={{
            mainWrapper: "w-[273px]",
            base: "w-[273px]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-[36px] w-[273px]",
          }}
        />

        {/* Status Filter */}
        <FilterSelect
          selectedKey={statusFilter}
          onSelectionChange={(value) => {
            setStatusFilter(value as 'all' | 'pending' | 'paid' | 'cancel');
            setCurrentPage(1);
          }}
          options={statusOptions}
          placeholder={t('withdrawal.status') || 'Статус'}
        />

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Tooltip content={t('filters.reset') || 'Сбросить фильтры'} placement="top">
            <Button
              size="sm"
              className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
              onPress={handleResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={requests}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default WithdrawalRequestsTab;
